/**
 * Supabase 클라이언트 초기화 모듈
 *
 * - 게시판(Board) 및 캘린더(Calendar) 기능의 데이터 레이어로 사용
 * - 인증은 Firebase Auth를 유지
 * - Supabase Third-party Auth (Firebase JWKS) 설정으로
 *   Firebase JWT를 accessToken 콜백으로 동적 주입하여 RLS 적용
 *
 * ⚠️ 주의: global.headers getter 패턴은 Supabase JS v2에서 createClient 시점에
 *   한번만 평가되어 Authorization 헤더가 고정됩니다.
 *   반드시 accessToken 콜백을 사용해야 매 요청마다 Firebase JWT가 주입됩니다.
 *
 * 로컬 에뮬레이터:
 *   Firebase Auth 에뮬레이터의 JWT는 `alg: "none"` (비서명)이라
 *   Supabase PostgREST가 PGRST301 에러를 반환합니다.
 *   에뮬레이터 환경에서는 JWT payload를 유지한 채
 *   Supabase 기본 JWT secret(HS256)으로 재서명하여 RLS가 정상 동작하도록 합니다.
 */
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** 로컬 에뮬레이터 환경 여부 (127.0.0.1 또는 localhost 대상) */
const isLocalEmulator = !!supabaseUrl?.match(/127\.0\.0\.1|localhost/);

/**
 * Supabase CLI 기본 JWT secret (supabase/config.toml 기본값)
 * 로컬 에뮬레이터 전용이므로 클라이언트에 포함해도 보안 문제 없음.
 */
const LOCAL_JWT_SECRET = 'super-secret-jwt-token-with-at-least-32-characters-long';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[Supabase] VITE_SUPABASE_URL 또는 VITE_SUPABASE_ANON_KEY가 설정되지 않았습니다. ' +
    'Supabase 기능(게시판/캘린더)은 비활성 상태입니다.'
  );
}

/**
 * 현재 Firebase JWT 토큰 (런타임에 동적 갱신)
 * onAuthStateChanged → setSupabaseFirebaseToken()으로 주입
 * accessToken 콜백에서 반환되어 requesting_user_id()로 UID 추출
 */
let currentFirebaseToken: string | null = null;

/** HS256 재서명 캐시 (원본 토큰 → 재서명된 토큰) */
let _resignedCache: { original: string; resigned: string } | null = null;

/**
 * Base64URL 인코딩 (JWT 표준)
 */
function base64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Firebase 에뮬레이터 JWT(alg:none)를 Supabase 기본 JWT secret으로 HS256 재서명.
 * payload의 sub(Firebase UID), role(authenticated) 등을 유지하여
 * PostgREST의 requesting_user_id()가 정상 동작하도록 합니다.
 */
async function resignForLocal(firebaseJwt: string): Promise<string> {
  // 캐시 히트
  if (_resignedCache?.original === firebaseJwt) return _resignedCache.resigned;

  try {
    const parts = firebaseJwt.split('.');
    const payload = JSON.parse(atob(parts[1]));

    // PostgREST가 인식하는 role claim 추가
    payload.role = 'authenticated';

    const newHeader = base64url(new TextEncoder().encode(JSON.stringify({ alg: 'HS256', typ: 'JWT' })));
    const newPayload = base64url(new TextEncoder().encode(JSON.stringify(payload)));
    const signingInput = `${newHeader}.${newPayload}`;

    // Web Crypto API로 HMAC-SHA256 서명
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(LOCAL_JWT_SECRET),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );
    const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(signingInput));
    const resigned = `${signingInput}.${base64url(sig)}`;

    _resignedCache = { original: firebaseJwt, resigned };
    return resigned;
  } catch (err) {
    console.warn('[Supabase] JWT 재서명 실패, 원본 반환:', err);
    return firebaseJwt;
  }
}

/**
 * 글로벌 Supabase 클라이언트 인스턴스
 * - env가 없으면 null (Firebase-only 환경에서도 앱이 정상 동작)
 *
 * accessToken 콜백으로 매 요청마다 Firebase JWT를 동적으로 주입:
 * 1. Firebase Auth currentUser가 있으면 → getIdToken()으로 최신 토큰 획득
 *    (캐시 유효 시 즉시 반환, 만료 시 자동 갱신 → JWT expired 방지)
 * 2. currentUser가 없으면 → fallback으로 저장된 토큰 사용
 *
 * 에뮬레이터 환경에서는 JWT를 HS256으로 재서명하여 PostgREST PGRST301 에러를 방지합니다.
 */
export const supabase: SupabaseClient<Database> | null =
  supabaseUrl && supabaseAnonKey
    ? createClient<Database>(supabaseUrl, supabaseAnonKey, {
        accessToken: async () => {
          // 1. Firebase Auth에서 최신 토큰을 직접 가져오기 (만료 시 자동 갱신)
          try {
            const { getAuth } = await import('firebase/auth');
            const auth = getAuth();
            if (auth.currentUser) {
              const freshToken = await auth.currentUser.getIdToken();
              // 캐시도 갱신
              currentFirebaseToken = freshToken;
            }
          } catch {
            // Firebase 미초기화 등의 경우 fallback
          }

          if (!currentFirebaseToken) return '';
          // 에뮬레이터: HS256 재서명 / 프로덕션: Firebase JWT 그대로 전달
          if (isLocalEmulator) return resignForLocal(currentFirebaseToken);
          return currentFirebaseToken;
        },
        auth: {
          // Firebase Auth를 사용하므로 Supabase 자체 세션 관리는 비활성화
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      })
    : null;

/**
 * Firebase ID Token을 저장
 * - Firebase Auth의 onAuthStateChanged 콜백에서 호출
 * - Third-party Auth 설정 완료 후 Supabase 요청 헤더에 주입 예정
 *
 * @param token Firebase ID Token (null이면 해제)
 */
export function setSupabaseFirebaseToken(token: string | null): void {
  currentFirebaseToken = token;
}

/** 현재 저장된 Firebase UID 반환 (클라이언트 사이드 접근 제어용) */
export function getFirebaseUid(): string | null {
  if (!currentFirebaseToken) return null;
  try {
    // JWT payload에서 sub(Firebase UID) 추출 (서명 검증 없이 디코딩만)
    const payload = currentFirebaseToken.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.sub ?? null;
  } catch {
    return null;
  }
}
